import { z } from 'zod';
import type { AgentDelegationDefaults } from './agents';
import {
  type AiCapabilities,
  type AiJobStatus,
  type AiProfile,
  type AiProviderId,
  type AiSettingsFields,
  aiReasoningEffortSchema,
  aiSettingsFieldsSchema,
} from './ai';
import type { ChatClientNotePreview } from './client-notes';
import { idSchema, localDateSchema } from './common';
import {
  type ContextProfileKind,
  contextProfileKindSchema,
  type MemoryProposalDto,
  memorySourceSchema,
} from './context';
import type { ChatPlanPreview, CopilotUndoStep } from './copilot-actions';
import { classifyMessageByRules, type EmailDraftView, MESSAGE_CLASSIFICATION_SEVERITIES } from './mail';
import type { TimeEntryParsePreview } from './time';
import { todayScopeSchema } from './today';

/**
 * Copilot operacyjny i Context Builder (MVP §11, §12, Etap 6, §17 nr 2 i 9; ARCHITECTURE §10; Fala 4).
 *
 * Copilot tworzy podsumowania, zadania, wpisy czasu, drafty maili i propozycje pamięci — zawsze jako JAWNE propozycje
 * albo dozwolone operacje przez te same usługi co UI (`via: 'ai'`), z uprawnieniami osoby, która go uruchomiła.
 * Wyłączone AI (`ai_settings.enabled = false`) albo awaria dostawcy nie blokują funkcji ręcznych. Historia: `ai_jobs`
 * (`kind` = akcja Copilota, dostawca, model, koszt, `simulated` w `output`).
 *
 * Context Builder wybiera minimum danych z warstw SYSTEM → OWNER → BUSINESS → CLIENT → PROJECT → CURRENT, podaje źródła
 * z datą i wersją, ogranicza rozmiar; treści klientów (maile, transkrypcje, notatki) opakowuje jako dane
 * (`wrapUntrustedData`). AI nie ma narzędzia do sejfu i nigdy nie dostaje sekretów.
 */

/** Akcje Copilota = `ai_jobs.kind` (rejestr kosztów i historia per akcja). */
export const COPILOT_ACTIONS = [
  'client_summary',
  'extract_tasks',
  'draft_email',
  'parse_time',
  'memory_proposal',
  'classify_message',
  'day_summary',
  'chat',
] as const;
export type CopilotAction = (typeof COPILOT_ACTIONS)[number];
export const copilotActionSchema = z.enum(COPILOT_ACTIONS);

/** Profil modelu per akcja (model z konfiguracji, nigdy w kodzie). */
export const COPILOT_ACTION_PROFILES: Readonly<Record<CopilotAction, AiProfile>> = {
  client_summary: 'smart',
  extract_tasks: 'smart',
  draft_email: 'smart',
  parse_time: 'fast',
  memory_proposal: 'smart',
  classify_message: 'fast',
  day_summary: 'fast',
  chat: 'smart',
};

/**
 * Możliwości adaptera wymagane przez akcję (MVP §12: brak wymaganej funkcji = jawny błąd, nie cicha degradacja).
 * Bramka sprawdza je przed wywołaniem dostawcy.
 */
export const COPILOT_REQUIRED_CAPABILITIES: Readonly<Record<CopilotAction, readonly (keyof AiCapabilities)[]>> = {
  client_summary: ['structuredOutput'],
  extract_tasks: ['structuredOutput'],
  draft_email: ['structuredOutput'],
  parse_time: ['structuredOutput'],
  memory_proposal: ['structuredOutput'],
  classify_message: ['structuredOutput'],
  day_summary: ['structuredOutput'],
  chat: ['tools'],
};

/** Warstwy kontekstu (MVP §11), w kolejności składania promptu. */
export const CONTEXT_LAYERS = ['system', 'owner', 'business', 'client', 'project', 'current'] as const;
export type ContextLayer = (typeof CONTEXT_LAYERS)[number];

/** Rodzaje źródeł kontekstu (odnośnik w wyniku: „na podstawie: notatka X z 3.10, wątek Y”). */
export const CONTEXT_SOURCE_TYPES = [
  'context_profile',
  'client',
  'contact',
  'project',
  'service',
  'task',
  'knowledge_item',
  'meeting',
  'offer',
  'message',
  'message_thread',
  'time_entry',
  'hosted_asset',
  'event',
  'approval',
  'text',
  /** Fala 10: notatka klienta (`client_notes`) */
  'client_note',
] as const;
export type ContextSourceType = (typeof CONTEXT_SOURCE_TYPES)[number];

export const contextSourceSchema = z.object({
  layer: z.enum(CONTEXT_LAYERS),
  type: z.enum(CONTEXT_SOURCE_TYPES),
  id: idSchema.nullable(),
  title: z.string().max(300),
  /** data źródła (Europe/Warsaw) */
  date: localDateSchema.nullable().optional(),
  /** wersja treści (profil, wiedza, oferta) */
  version: z.number().int().positive().nullable().optional(),
  /** treść od klienta / osób trzecich — w prompcie jako dane, nie polecenia */
  untrusted: z.boolean().default(false),
});
export type ContextSource = z.infer<typeof contextSourceSchema>;

/** Limity Context Buildera (znaki): minimum danych, bez kopiowania całej bazy. */
export const CONTEXT_LIMITS = {
  totalChars: 24_000,
  perSourceChars: 4_000,
  maxSources: 24,
  /** podobne przykłady wiadomości (MVP §11: 2–3, bez danych innego klienta) */
  maxExamples: 3,
} as const;

/** Jawny brak danych w kontekście (zamiast zgadywania). */
export const CONTEXT_NO_DATA = 'brak danych';

/**
 * Nagłówek źródła w prompcie: `### [knowledge_item:<id>] Decyzja: Formularz kontaktowy · 3.10.2026 · wersja 2`.
 * Jeden format dla Context Buildera, adaptera fake i testów (model widzi typ, id, datę i wersję każdego źródła).
 */
export function formatSourceHeader(s: Pick<ContextSource, 'type' | 'id' | 'title' | 'date' | 'version'>): string {
  const parts = [s.title.replace(/[\r\n]+/g, ' ').trim()];
  if (s.date) {
    const [y, m, d] = s.date.split('-');
    parts.push(`${Number(d)}.${m}.${y}`);
  }
  if (s.version) parts.push(`wersja ${s.version}`);
  return `### [${s.type}:${s.id ?? '-'}] ${parts.join(' · ')}`;
}

const SOURCE_HEADER_RE = /^### \[([a-z_]+):([0-9a-f-]{36}|-)\] (.+)$/gm;

/** Nagłówki źródeł z promptu (adapter fake, testy). */
export function parseSourceHeaders(text: string): { type: string; id: string | null; title: string }[] {
  const out: { type: string; id: string | null; title: string }[] = [];
  for (const m of text.matchAll(SOURCE_HEADER_RE)) {
    out.push({ type: m[1] ?? '', id: m[2] && m[2] !== '-' ? m[2] : null, title: (m[3] ?? '').split(' · ')[0] ?? '' });
  }
  return out;
}

/** Linia skrótu dnia w prompcie „Podsumuj dzień” (adapter fake odpowiada nią wprost). */
export const DAY_DIGEST_PREFIX = 'Skrót dnia: ';

/** Wynik klasyfikacji maila przez AI (bez `approval`); niepoprawny wynik = mail zostaje „Zwykłe” w Inboxie. */
export const messageClassificationResultSchema = z.object({
  severity: z.enum(MESSAGE_CLASSIFICATION_SEVERITIES),
  reason: z.string().trim().min(1).max(300),
  /** pewność 0–1; „Pilne” (critical) tylko przy wysokiej pewności i słowach o awarii / utracie danych */
  confidence: z.number().min(0).max(1).default(0.5),
  /** istotność (17.10.2026): mail wymaga uwagi osoby; osobny wymiar od „critical” (`MAIL_IMPORTANT_DEFINITION`) */
  important: z.boolean().optional(),
});
export type MessageClassificationResult = z.infer<typeof messageClassificationResultSchema>;

/** Próg pewności dla „Pilne” z klasyfikacji maila (MVP §9, §14: Critical = wiarygodne pilne zgłoszenie). */
export const CLASSIFICATION_CRITICAL_MIN_CONFIDENCE = 0.8;

/** Wynik draftu maila: treść + jawne założenia i pytania (zamiast dopisywania faktów). */
export const emailDraftResultSchema = z.object({
  subject: z.string().trim().min(1).max(998),
  bodyText: z.string().trim().min(1).max(20_000),
  assumptions: z.array(z.string().max(300)).max(10).default([]),
  questions: z.array(z.string().max(300)).max(10).default([]),
});
export type EmailDraftResult = z.infer<typeof emailDraftResultSchema>;

/** Podsumowanie klienta („W skrócie”): fakty ze źródłami, otwarte kwestie; bez źródła = nie fakt. */
export const clientSummaryResultSchema = z.object({
  summary: z.string().trim().min(1).max(2_000),
  facts: z
    .array(z.object({ text: z.string().trim().min(1).max(500), sourceIds: z.array(idSchema).max(10).default([]) }))
    .max(20)
    .default([]),
  openQuestions: z.array(z.string().max(300)).max(10).default([]),
});
export type ClientSummaryResult = z.infer<typeof clientSummaryResultSchema>;

/** Zadania z tekstu / maila (propozycje do przeglądu, nie zadania). */
export const extractedTasksResultSchema = z.object({
  tasks: z
    .array(
      z.object({
        title: z.string().trim().min(1).max(300),
        description: z.string().trim().max(2_000).nullable().optional(),
        dueHint: z.string().trim().max(100).nullable().optional(),
      }),
    )
    .max(20)
    .default([]),
});
export type ExtractedTasksResult = z.infer<typeof extractedTasksResultSchema>;

/** Czas z tekstu (AI); podgląd i tak liczy `time.parse` (te same reguły co formularz). */
export const parsedTimeResultSchema = z.object({
  clientHint: z.string().trim().max(200).nullable().default(null),
  start: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .default(null),
  end: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .default(null),
  durationSeconds: z.number().int().min(60).max(86_400).nullable().default(null),
  description: z.string().trim().max(500).default(''),
});
export type ParsedTimeResult = z.infer<typeof parsedTimeResultSchema>;

/** Zasada do zapamiętania („zapamiętaj …”) — jedna krótka zasada, profil docelowy. */
export const memoryRuleResultSchema = z.object({
  rule: z.string().trim().min(3).max(500),
  profileKind: contextProfileKindSchema.default('owner'),
});
export type MemoryRuleResult = z.infer<typeof memoryRuleResultSchema>;

/** Podsumowanie dnia (Dziś → „Podsumuj dzień”). */
export const daySummaryResultSchema = z.object({
  summary: z.string().trim().min(1).max(1_500),
  highlights: z.array(z.string().trim().max(300)).max(8).default([]),
});
export type DaySummaryAiResult = z.infer<typeof daySummaryResultSchema>;

/** Status wywołania Copilota w UI. */
export const COPILOT_RESULT_STATES = ['ok', 'disabled', 'budget_exceeded', 'unavailable', 'failed'] as const;
export type CopilotResultState = (typeof COPILOT_RESULT_STATES)[number];

export const COPILOT_MESSAGES = {
  taskNeedsConfirmation:
    'Rozmowa zawiera dane klienta (np. treść maila), więc zadanie czeka na Twoje potwierdzenie — sprawdź tytuł i opis.',
  disabled: 'AI jest wyłączone. Funkcje ręczne działają bez zmian.',
  /** limit AI osoby 0 zł przy szablonie Partner (DECISIONS 2026-10-08 [S] „Reguła partnerów”) */
  partnerAiOff:
    "Jako partner korzystasz z AI we własnym workspace'ie (własny klucz i budżet). Przełącz się do swojego workspace'u.",
  /** limit AI osoby 0 zł (bez szablonu Partner) */
  memberAiOff: "Administrator wyłączył Ci AI w tym workspace'ie.",
  budgetExceeded: 'Limit kosztów AI na ten miesiąc został wykorzystany. Funkcje ręczne działają bez zmian.',
  unavailable: 'AI jest chwilowo niedostępne. Spróbuj ponownie albo zrób to ręcznie.',
  simulated: 'Symulacja AI — odpowiedź z adaptera testowego, nic nie zostało wysłane do dostawcy.',
  untrustedInstruction: 'Treść maila zawiera polecenia dla AI — potraktowano je jako dane klienta.',
  secretRefusal:
    'Nie mam dostępu do haseł ani sejfu i nie podaję dostępów. Dostęp zobaczysz w Utrzymaniu → zasób → Dostępy, po potwierdzeniu tożsamości.',
  noClientData: 'Brak danych o ustaleniach i bieżących sprawach klienta.',
  invalidResult: 'AI zwróciło wynik w nieznanym formacie. Spróbuj ponownie albo zrób to ręcznie.',
  ambiguousClient: 'Pasuje kilku klientów — wybierz właściwego.',
  sendNotAvailable: 'Wysyłka maili przez akceptację nie jest jeszcze dostępna w tym środowisku.',
  rulesFallback: 'Bez AI — wynik z reguł (ten sam parser co formularz).',
  /** Fala 9: podgląd zlecenia dla Claude (`delegate_to_claude`) */
  delegateNeedsConfirmation:
    'Copilot nie zmienia kodu ani repozytoriów — przygotował zlecenie dla Claude. Nic nie zostało zlecone: sprawdź instrukcję i kliknij „Zleć Claude”.',
  delegateUntrusted: 'Rozmowa zawiera dane klienta (np. treść maila) — upewnij się, że instrukcja jest Twoja.',
  /** przegląd Fali 9: długa instrukcja albo rozmowa z danymi klienta = zlecenie tylko z pełnego okna */
  delegateReviewInDialog:
    'Instrukcja jest długa albo powstała z rozmowy z danymi klienta — zlecisz ją z pełnego okna: kliknij „Sprawdź i zleć…”, przeczytaj całość i popraw, jeśli trzeba.',
} as const;

/**
 * Szybkie potwierdzenie „Zleć Claude” jednym kliknięciem w odpowiedzi Copilota tylko dla krótkiej instrukcji
 * z rozmowy bez danych klienta (przegląd Fali 9: karta pokazywała 6 linii, a kliknięcie zlecało całość — do 20 000
 * znaków, w tym ewentualne polecenia wstrzyknięte mailem). Dłuższa albo z danymi klienta = tylko pełne okno z polem
 * tekstowym („Sprawdź i zleć…”).
 */
export const DELEGATE_QUICK_CONFIRM_MAX_CHARS = 600;
export const DELEGATE_QUICK_CONFIRM_MAX_LINES = 8;

export function delegateNeedsFullReview(preview: { instruction: string; untrustedContext: boolean }): boolean {
  if (preview.untrustedContext) return true;
  const text = preview.instruction;
  return text.length > DELEGATE_QUICK_CONFIRM_MAX_CHARS || text.split('\n').length > DELEGATE_QUICK_CONFIRM_MAX_LINES;
}

// ── Reguły bez AI (adapter fake, klasyfikacja zapasowa, strażnik wyników) ───

const OUTAGE_RE =
  /(nie\s+dzia[łl]a|przesta[łl]\p{L}*\s+dzia[łl]a|awari\p{L}*|pad[łl]\p{L}*|nie\s+odpowiada|niedost[ęe]pn\p{L}*|nie\s+(?:otwiera|[łl]aduje|wczytuje)\s+si[ęe]|b[łl][ąa]d\s+(?:5\d\d|serwera|krytyczn\p{L}*)|\b50[0-4]\b|\bdown\b|\boutage\b)/iu;
const DATA_LOSS_RE =
  /(utrat\p{L}*\s+danych|strac\p{L}*\s+(?:wszystkie\s+)?dane|znikn[ęe]\p{L}*\s+(?:wszystkie\s+)?(?:dane|zam[óo]wieni\p{L}*|wpisy|tre[śs]ci|pliki)|usuni[ęe]t\p{L}*\s+(?:dane|baz\p{L}*|pliki)|wyciek\p{L}*|w[łl]amani\p{L}*|zhakowan\p{L}*|ransomware|zaszyfrowan\p{L}*\s+(?:pliki|dane)|data\s+loss|data\s+breach)/iu;
const URGENT_RE = /(pilne|pilnie|natychmiast|asap|jak\s+najszybciej|krytyczn\p{L}*|od\s+rana)/iu;
const THANKS_RE = /^\s*(?:dzi[ęe]kuj[ęe]|dzi[ęe]ki|dzi[ęe]kujemy|thanks|thank\s+you)\b/iu;
const REQUEST_RE =
  /(\?|czy\s+(?:mo[żz]ecie|mo[żz]esz|mo[żz]na|da\s+si[ęe]|m[óo]g[łl]by)|prosz[ęe]|pro[śs]b\p{L}*|potrzebuj\p{L}*|chcieli(?:by)?(?:[śs]my)?|zale[żz]y\s+nam|przygotuj\p{L}*|wycen\p{L}*|doda[ćc]|zrobi[ćc]|zmieni[ćc]|popraw\p{L}*)/iu;
const INJECTION_RE =
  /(zignoruj\s+(?:wszystkie\s+|poprzednie\s+|wcze[śs]niejsze\s+|powy[żz]sze\s+|swoje\s+)*(?:instrukcj\p{L}*|polece\p{L}*|zasady|regu[łl]\p{L}*)|ignore\s+(?:all\s+|any\s+|the\s+)?(?:previous\s+|prior\s+|above\s+)?instructions|disregard\s+(?:all|previous|prior)|system\s*prompt|jeste[śs]\s+teraz|you\s+are\s+now|wy[śs]lij\s+mi\s+(?:dane|list[ęe]|wszystk\p{L}*|has[łl]\p{L}*|dost[ęe]p\p{L}*)|(?:dane|list\p{L}*|adresy|kontakty)\s+wszystkich\s+klient\p{L}*)/iu;
const SECRET_NOUN_RE =
  /(has[łl]\p{L}*|password\p{L}*|passwd|dane\s+(?:do\s+)?logowania|dost[ęe]p\p{L}*\s+do\s+(?:ftp|sftp|ssh|serwer\p{L}*|panel\p{L}*|hosting\p{L}*|baz\p{L}*|wp-admin|poczty|skrzynki)|\b(?:ftp|sftp|ssh)\b|klucz\p{L}*\s+(?:api|prywatn\p{L}*|ssh)|api[\s_-]?key|sekret\p{L}*|credential\p{L}*|\bpin\b)/iu;
const REVEAL_VERB_RE =
  /(?<![\p{L}])(podaj|poka[żz]|wy[śs]lij|daj|przypomnij|odczytaj|ujawnij|skopiuj|przeka[żz]|jakie\s+(?:jest|s[ąa])|jaki\s+jest|zdrad[źz]|give|show|send|tell|reveal|what\s+is)(?![\p{L}])/iu;

/** Słowa o awarii albo utracie danych (MVP §14): bez nich klasyfikacja nie może dać „Pilne”. */
export function mentionsOutageOrDataLoss(text: string): boolean {
  return OUTAGE_RE.test(text) || DATA_LOSS_RE.test(text);
}

/** Polecenia skierowane do AI w treści od klienta („zignoruj instrukcje”, „wyślij mi dane wszystkich klientów”). */
export function detectsUntrustedInstructions(text: string): boolean {
  return INJECTION_RE.test(text);
}

/** Prośba o hasło, dostęp albo klucz (AI nie ma sejfu: odmowa bez sekretu). */
export function isSecretRequest(text: string): boolean {
  return SECRET_NOUN_RE.test(text) && (REVEAL_VERB_RE.test(text) || text.includes('?'));
}

/** Tekst maila bez cytowanych linii odpowiedzi (`>`). */
function withoutQuotes(text: string): string {
  return text
    .split(/\r?\n/)
    .filter((l) => !l.trimStart().startsWith('>'))
    .join('\n');
}

/**
 * Klasyfikacja maila regułami (adapter fake, AI wyłączone przy jawnym wywołaniu): awaria / utrata danych → Pilne
 * (pewność 0,85–0,92), samo podziękowanie → FYI, reszta → Zwykłe. Polecenia dla AI w treści obniżają pewność.
 */
function simulateSeverity(m: { subject: string; bodyText: string }): MessageClassificationResult {
  const body = withoutQuotes(m.bodyText);
  const text = `${m.subject}\n${body}`;
  const injection = detectsUntrustedInstructions(text);
  if (mentionsOutageOrDataLoss(text)) {
    const dataLoss = DATA_LOSS_RE.test(text);
    const confidence = injection ? 0.6 : URGENT_RE.test(text) ? 0.92 : 0.85;
    return {
      severity: 'critical',
      reason: dataLoss ? 'Zgłoszenie utraty danych' : 'Zgłoszenie awarii strony lub usługi',
      confidence,
    };
  }
  const plain = body.replace(/\s+/g, ' ').trim();
  if (THANKS_RE.test(plain) && plain.length <= 160 && !REQUEST_RE.test(plain)) {
    return { severity: 'fyi', reason: 'Podziękowanie bez prośby — do historii', confidence: 0.8 };
  }
  if (injection) {
    return {
      severity: 'normal',
      reason: 'Prośba o dane lub polecenie w treści — decyduje osoba; treść maila to dane, nie polecenie',
      confidence: 0.7,
    };
  }
  return {
    severity: 'normal',
    reason: REQUEST_RE.test(text) ? 'Zwykłe zapytanie lub prośba klienta' : 'Zwykła wiadomość',
    confidence: 0.7,
  };
}

/**
 * Klasyfikacja maila w symulacji (adapter fake): ważność jak wyżej, istotność z reguł poczty (`classifyMessageByRules`:
 * nadawca pisze „ważne / pilne”, pytanie, prośba; samo podziękowanie — mniej ważne). Pilne zawsze istotne.
 */
export function simulateMessageClassification(m: {
  subject: string;
  bodyText: string;
  /** nadawca (linia „Od:” bloku wiadomości): automat / newsletter = mniej ważny jak w regułach */
  from?: { email: string } | null;
}): MessageClassificationResult {
  const base = simulateSeverity(m);
  const rules = classifyMessageByRules(m);
  const important = base.severity === 'critical' || (base.severity !== 'fyi' && rules.important) || rules.declared;
  return { ...base, important };
}

/**
 * Strażnik wyniku klasyfikacji: „Pilne” tylko przy pewności ≥ 0,8 i słowach o awarii / utracie danych w treści.
 * Inaczej „Zwykłe” z adnotacją (mail zostaje w Inboxie, bez push).
 */
export function guardMessageClassification(
  result: MessageClassificationResult,
  text: string,
): MessageClassificationResult {
  if (result.severity !== 'critical') return result;
  if (result.confidence >= CLASSIFICATION_CRITICAL_MIN_CONFIDENCE && mentionsOutageOrDataLoss(text)) return result;
  return {
    severity: 'normal',
    reason: `${result.reason} (bez potwierdzonej awarii — Zwykłe)`.slice(0, 300),
    confidence: result.confidence,
    // możliwa awaria bez potwierdzenia nadal wymaga uwagi osoby (istotny)
    important: true,
  };
}

const SECRET_VALUE_PATTERNS: [RegExp, string][] = [
  // „hasło: …”, „token=…”, „hasło do FTP: …” — najpierw, żeby wartość znikała w całości
  [
    /(?<![\p{L}])(has[łl]\p{L}*|password|passwd|pwd|pass|token|api[ \t_-]?key|klucz(?:[ \t]+api)?|secret|sekret|pin)((?:[ \t]+[\p{L}\d._@/-]{1,40}){0,3}[ \t]*[:=][ \t]*)(?!\[ukryt)\S+/giu,
    '$1$2[ukryte]',
  ],
  [/-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----[\s\S]*?(?:-----END [A-Z0-9 ]*PRIVATE KEY-----|$)/g, '[ukryty klucz]'],
  [/\bsk-ant-[A-Za-z0-9_-]{8,}/g, '[ukryty klucz]'],
  [/\b(?:sk|pk|rk)-[A-Za-z0-9_-]{16,}/g, '[ukryty klucz]'],
  [/\bgh[pousr]_[A-Za-z0-9]{20,}/g, '[ukryty klucz]'],
  [/\bxox[abprs]-[A-Za-z0-9-]{10,}/g, '[ukryty klucz]'],
  [/\bAKIA[0-9A-Z]{16}\b/g, '[ukryty klucz]'],
  [/\bfos_(?:pat|oat|ort)_[A-Za-z0-9_-]{8,}/g, '[ukryty klucz]'],
  // schemat adresu ograniczony do 32 znaków: czas liniowy (audyt Fali 6, ReDoS — „a.a.a.…” dawało czas kwadratowy)
  [/\b([a-z][a-z0-9+.-]{0,31}:\/\/)[^\s/:@]+:[^\s/@]+@/gi, '$1[ukryte]@'],
];

/**
 * Twardy filtr sekretów w tekście dla AI, logu i historii (hasła, klucze API, tokeny, klucze prywatne, dane logowania
 * w adresie URL). Sejf nie trafia do kontekstu wcale (Context Builder go nie czyta); to druga warstwa dla wpisanych
 * ręcznie notatek i wiadomości.
 */
export function scrubSecrets(text: string): string {
  let out = text;
  for (const [re, replacement] of SECRET_VALUE_PATTERNS) out = out.replace(re, replacement);
  return out;
}

// ── Pamięć („zapamiętaj …”) ─────────────────────────────────────────────────

/** Sekcja profilu z zasadami dodanymi poleceniem „zapamiętaj”. */
export const MEMORY_RULES_HEADING = '## Zapamiętane';

/** „zapamiętaj, że wolę krótkie maile” → „Wolę krótkie maile.” */
export function normalizeRememberText(text: string): string {
  const stripped = text
    .trim()
    .replace(
      /^(?:prosz[ęe]\s+)?(?:zapami[ęe]taj|pami[ęe]taj|zanotuj|remember)\s*(?:[,:–—-]\s*)?(?:(?:[żz]e|to|that)\b\s*)?/iu,
      '',
    )
    .replace(/\s+/g, ' ')
    .trim();
  if (!stripped) return '';
  const capital = stripped.charAt(0).toUpperCase() + stripped.slice(1);
  return /[.!?…]$/.test(capital) ? capital : `${capital}.`;
}

/** Nowa treść profilu z zasadą dopisaną w sekcji „Zapamiętane” (bez duplikatu tej samej zasady). */
export function appendMemoryRule(body: string, rule: string): string {
  const line = `- ${rule.trim()}`;
  const text = body.replace(/\r\n?/g, '\n').trimEnd();
  if (text.split('\n').some((l) => l.trim().toLowerCase() === line.toLowerCase())) return `${text}\n`;
  const lines = text.length ? text.split('\n') : [];
  const at = lines.findIndex((l) => l.trim() === MEMORY_RULES_HEADING);
  if (at === -1) return `${text}${text ? '\n\n' : ''}${MEMORY_RULES_HEADING}\n\n${line}\n`;
  let end = lines.length;
  for (let i = at + 1; i < lines.length; i++) {
    if (/^#{1,2}\s/.test(lines[i] ?? '')) {
      end = i;
      break;
    }
  }
  let insertAt = end;
  while (insertAt > at + 1 && (lines[insertAt - 1] ?? '').trim() === '') insertAt--;
  lines.splice(insertAt, 0, line);
  return `${lines.join('\n').trimEnd()}\n`;
}

// ── Wejścia usług (moduł copilot, router `copilot`) ─────────────────────────

/** Bieżący widok, z którego otwarto „Zapytaj AI” (kontekst CURRENT). */
export const COPILOT_CONTEXT_REF_TYPES = [
  'client',
  'project',
  'task',
  'thread',
  'message',
  'meeting',
  'draft',
] as const;
export type CopilotContextRefType = (typeof COPILOT_CONTEXT_REF_TYPES)[number];
export const copilotContextRefSchema = z.object({ type: z.enum(COPILOT_CONTEXT_REF_TYPES), id: idSchema }).strict();
export type CopilotContextRef = z.infer<typeof copilotContextRefSchema>;

export const copilotStatusInput = z.object({}).strict().optional();

export const summarizeClientInput = z.object({ clientId: idSchema }).strict();
export type SummarizeClientInput = z.input<typeof summarizeClientInput>;
export const clientSummaryInput = summarizeClientInput;

export const draftReplyInput = z
  .object({
    threadId: idSchema,
    /** dodatkowe wskazówki osoby („zaproponuj czwartek”) */
    instructions: z.string().trim().max(1_000).optional(),
  })
  .strict();
export type DraftReplyInput = z.input<typeof draftReplyInput>;

export const tasksFromTextInput = z
  .object({
    text: z.string().trim().min(1, { message: 'Wpisz tekst' }).max(20_000).optional(),
    messageId: idSchema.optional(),
    meetingId: idSchema.optional(),
    clientId: idSchema.nullable().optional(),
    projectId: idSchema.nullable().optional(),
  })
  .strict()
  .refine((v) => [v.text, v.messageId, v.meetingId].filter((x) => x !== undefined).length === 1, {
    message: 'Podaj tekst, wiadomość albo spotkanie',
  });
export type TasksFromTextInput = z.input<typeof tasksFromTextInput>;

export const COPILOT_PROPOSAL_SOURCES = ['message', 'ai'] as const;
export type CopilotProposalSource = (typeof COPILOT_PROPOSAL_SOURCES)[number];

export const listTaskProposalsInput = z
  .object({ sourceType: z.enum(COPILOT_PROPOSAL_SOURCES), sourceId: idSchema })
  .strict();

export const decideTaskProposalsInput = z
  .object({
    accept: z
      .array(
        z
          .object({
            id: idSchema,
            version: z.number().int().positive().optional(),
            title: z.string().trim().min(1).max(300).optional(),
            assigneeUserId: idSchema.nullable().optional(),
            dueDate: localDateSchema.nullable().optional(),
          })
          .strict(),
      )
      .max(50)
      .default([]),
    reject: z.array(idSchema).max(50).default([]),
  })
  .strict()
  .refine((v) => v.accept.length + v.reject.length > 0, { message: 'Wybierz propozycje' });
export type DecideTaskProposalsInput = z.input<typeof decideTaskProposalsInput>;

export const timeEntryFromTextInput = z
  .object({
    text: z.string().trim().min(1, { message: 'Wpisz zdanie' }).max(500, { message: 'Najwyżej 500 znaków' }),
    clientId: idSchema.optional(),
    confirmEndNextDay: z.boolean().optional(),
  })
  .strict();
export type TimeEntryFromTextInput = z.input<typeof timeEntryFromTextInput>;

export const proposeMemoryUpdateInput = z
  .object({
    text: z.string().trim().min(3, { message: 'Napisz, co zapamiętać' }).max(2_000),
    profileKind: contextProfileKindSchema.optional(),
    source: memorySourceSchema.optional(),
  })
  .strict();
export type ProposeMemoryUpdateInput = z.input<typeof proposeMemoryUpdateInput>;

export const summarizeDayInput = z
  .object({ scope: todayScopeSchema.default('mine') })
  .strict()
  .default({});

export const COPILOT_CHAT_MAX_MESSAGES = 20;
export const COPILOT_CHAT_MAX_CHARS = 4_000;

export const chatMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string().trim().min(1).max(COPILOT_CHAT_MAX_CHARS, { message: 'Wiadomość jest za długa' }),
});
export type CopilotChatMessage = z.infer<typeof chatMessageSchema>;

export const copilotChatInput = z
  .object({
    messages: z.array(chatMessageSchema).min(1).max(COPILOT_CHAT_MAX_MESSAGES),
    contextRef: copilotContextRefSchema.nullable().optional(),
    /**
     * Fala 10: identyfikator rozmowy (UUID z przeglądarki, nowy po „Nowa rozmowa”) — idempotencja działań w obrębie
     * rozmowy: powtórzone polecenie nie tworzy duplikatu. Bez niego: klucz z pierwszej wiadomości rozmowy.
     */
    conversationId: idSchema.optional(),
  })
  .strict()
  .refine((v) => v.messages[v.messages.length - 1]?.role === 'user', {
    message: 'Ostatnia wiadomość musi być od Ciebie',
  });
export type CopilotChatInput = z.input<typeof copilotChatInput>;

// ── Narzędzia „Zapytaj AI” (te same usługi co UI, `via: 'ai'`; BEZ narzędzi sejfu) ─────────────

export const COPILOT_TOOL_NAMES = [
  'search',
  'get_client_context',
  'list_tasks',
  'create_task',
  'log_time',
  'draft_email',
  'propose_send_email',
  'propose_memory_update',
  /**
   * Fala 9 (DECISIONS 2026-10-07 „Zmiany w kodzie i operacje robi tylko Claude”): Copilot NIE zmienia kodu ani
   * repozytoriów — przygotowuje zlecenie dla Claude (PODGLĄD: tytuł, instrukcja, kontekst, wykonawca); przebieg tworzy
   * osoba przyciskiem „Zleć Claude” w odpowiedzi (`agents.create` z UI). `agents.create` ma `denyVia: ['ai']`.
   */
  'delegate_to_claude',
  // Fala 10 (DECISIONS 2026-10-07 „Copilot ma być prawdziwym asystentem”; @fos/shared/contracts/copilot-actions):
  // działania na jednoznaczne polecenie od razu (link + „Cofnij”), przy braku danych / niejednoznaczności — plan
  // do potwierdzenia; `run_plan` = kilka działań z jednego polecenia (kolejne kroki odwołują się do wyników poprzednich)
  'create_client',
  'add_contact',
  'update_task',
  'add_note',
  'create_meeting',
  'add_monitor',
  'list_incidents',
  'explain_incident',
  'run_plan',
  // Fala 10 (tor notes-history): notatka na kliencie (oś czasu „Historia”), z linkiem i „Cofnij” (archiwizacja)
  'add_client_note',
  // 17.10.2026: „zanotuj …” / „zapisz na karteczce …” = szybka notatka osoby na „Dziś” (nie zadanie), z „Cofnij”
  'add_quick_note',
] as const;
export type CopilotToolName = (typeof COPILOT_TOOL_NAMES)[number];

/** Narzędzia, które zmieniają dane (osobno pilnowane w testach prompt injection). */
export const COPILOT_MUTATING_TOOLS = [
  'create_task',
  'draft_email',
  'propose_send_email',
  'propose_memory_update',
  'create_client',
  'add_contact',
  'update_task',
  'add_note',
  'create_meeting',
  'add_monitor',
  'run_plan',
  'add_client_note',
  'add_quick_note',
] as const satisfies readonly CopilotToolName[];

// ── DTO ─────────────────────────────────────────────────────────────────────

/** Skąd jest wynik: AI (także symulacja) albo reguły bez AI (fallback). */
export type CopilotResultSource = 'ai' | 'rules';

export interface CopilotRunMeta {
  aiJobId: string | null;
  provider: string;
  model: string | null;
  /** adapter fake: „Symulacja — nic nie zostało wysłane do dostawcy” */
  simulated: boolean;
  source: CopilotResultSource;
  generatedAt: Date;
  /** koszt w PLN (tylko z „Widzi stawki i kwoty”) */
  costPln?: string;
  /** wersja kontekstu (`ai_jobs.context_version`) */
  contextVersion: string | null;
  /** powód wyniku z reguł (AI wyłączone, budżet, niedostępne) */
  fallbackReason?: string | null;
}

export type AiProviderState = 'simulated' | 'connected' | 'not_connected' | 'auth_lost' | 'error';
export const AI_PROVIDER_STATES = [
  'simulated',
  'connected',
  'not_connected',
  'auth_lost',
  'error',
] as const satisfies readonly AiProviderState[];

export interface CopilotStatus {
  enabled: boolean;
  provider: string;
  state: AiProviderState;
  simulated: boolean;
  classifyIncoming: boolean;
  /** czy Copilot może teraz działać (AI włączone i dostawca dostępny) */
  available: boolean;
  /** komunikat dla UI, gdy niedostępny albo w symulacji */
  message: string | null;
  /**
   * AI wyłączone dla tej osoby w tym workspace'ie (limit AI osoby 0 zł): `partner` = szablon Partner (AI we własnym
   * workspace'ie), `member` = wyłączył administrator. Wtedy `available = false`, a `message` mówi dlaczego.
   */
  memberBlock?: 'partner' | 'member' | null;
}

export interface ClientSummaryDto {
  clientId: string;
  summary: string;
  facts: { text: string; sourceIds: string[] }[];
  openQuestions: string[];
  /** w kontekście nie było danych o ustaleniach i bieżących sprawach */
  noData: boolean;
  sources: ContextSource[];
  meta: CopilotRunMeta;
}

export interface DraftReplyResult {
  draft: EmailDraftView;
  assumptions: string[];
  questions: string[];
  /** ostrzeżenia (polecenia w treści maila, brak klienta) */
  warnings: string[];
  sources: ContextSource[];
  meta: CopilotRunMeta;
}

export interface CopilotTaskProposal {
  id: string;
  version: number;
  sourceType: CopilotProposalSource | 'meeting';
  sourceId: string;
  title: string;
  description: string | null;
  clientId: string | null;
  projectId: string | null;
  assigneeUserId: string | null;
  dueDate: string | null;
  status: 'proposed' | 'accepted' | 'rejected';
  taskId: string | null;
}

export interface TasksFromTextResult {
  mode: 'proposals' | 'meeting_queued';
  sourceType: CopilotProposalSource | 'meeting';
  sourceId: string;
  proposals: CopilotTaskProposal[];
  created: number;
  existing: number;
  warnings: string[];
  meta: CopilotRunMeta | null;
}

export interface DecideTaskProposalsResult {
  accepted: { proposalId: string; taskId: string; taskNumber: number }[];
  rejected: string[];
}

export interface TimeFromTextResult {
  preview: TimeEntryParsePreview;
  meta: CopilotRunMeta;
}

export interface MemoryUpdateResult {
  proposal: MemoryProposalDto;
  approvalId: string | null;
  rule: string;
  profileKind: ContextProfileKind;
  meta: CopilotRunMeta;
}

export interface DaySummaryDto {
  summary: string;
  highlights: string[];
  sources: ContextSource[];
  meta: CopilotRunMeta;
}

export type ChatToolEventStatus = 'ok' | 'error' | 'needs_confirmation' | 'waiting_approval' | 'needs_choice';

export interface ChatToolEvent {
  tool: CopilotToolName;
  status: ChatToolEventStatus;
  /** krótki opis po polsku („Dodano zadanie #12 Sekcja FAQ”) */
  label: string;
  /**
   * rekord wyniku (zadanie, szkic, akceptacja, propozycja pamięci, klient; Fala 10: notatka klienta — `clientId` do
   * adresu profilu klienta, bo notatka nie ma własnej strony)
   */
  link: { type: string; id: string; clientId?: string } | null;
  approvalId?: string | null;
  /** Fala 10 (`add_client_note`): notatka klienta do potwierdzenia w UI (rozmowa z danymi klienta) — nic nie zapisano */
  notePreview?: ChatClientNotePreview;
  /** `log_time`: podgląd do potwierdzenia w UI (nic nie jest zapisane) */
  timePreview?: TimeEntryParsePreview;
  /**
   * `create_task` w rozmowie z danymi klienta (maile, notatki, spotkania — możliwe polecenia w treści): zadanie do
   * potwierdzenia przez osobę w UI (`tasks.create`), nic nie jest zapisane
   */
  taskPreview?: ChatTaskPreview;
  /** `delegate_to_claude` (Fala 9): zlecenie dla Claude do potwierdzenia przez osobę — nic nie jest zlecone */
  delegatePreview?: ChatDelegatePreview;
  /** `needs_choice`: kandydaci do wyboru */
  choices?: { id: string; label: string }[];
  /**
   * Fala 10: działanie wykonane — kroki „Cofnij” (`copilot.undo`, kolejno: archiwizacja utworzonego rekordu albo
   * przywrócenie poprzednich wartości zadania). Brak = nieodwracalne albo nic nie zmieniono.
   */
  undo?: CopilotUndoStep[];
  /** Fala 10: działanie było już wykonane w tej rozmowie (idempotencja) — bez nowego rekordu */
  repeated?: boolean;
  /** 10.10.2026: osoba przypisana do dodanego zadania (dopisek „Przypisane do …”, gdy to ktoś inny) */
  assignee?: { userId: string; name: string } | null;
  /**
   * Fala 10: plan działań do potwierdzenia (brak danych, niejednoznaczność, dane firmy po NIP, stawka spoza polecenia,
   * dane klienta w rozmowie) — nic nie jest zapisane do kliknięcia „Wykonaj” (`copilot.executePlan` z UI)
   */
  plan?: ChatPlanPreview;
}

/**
 * Podgląd zlecenia dla Claude z narzędzia `delegate_to_claude`: tytuł, instrukcja, kontekst i wykonawca
 * (`agents.delegationPreview`: lokalny runner przy aktywnym komputerze z repozytorium projektu, inaczej sesja Claude
 * przez MCP). Przebieg powstaje dopiero po kliknięciu „Zleć Claude” przez osobę.
 */
export interface ChatDelegatePreview extends AgentDelegationDefaults {
  title: string;
  instruction: string;
  /** rozmowa zawiera dane klienta (maile, notatki) — instrukcja mogła z nich pochodzić; osoba sprawdza ją przed zleceniem */
  untrustedContext: boolean;
}

export interface ChatTaskPreview {
  title: string;
  descriptionMd: string | null;
  clientId: string | null;
  clientName: string | null;
  projectId: string | null;
  assigneeUserId: string | null;
  dueDate: string | null;
  priority: string | null;
}

/** Stan AI widoczny w UI: AI pracuje (zapytanie trwa) / gotowe / czeka na akceptację / czeka na potwierdzenie. */
export type ChatState = 'done' | 'needs_confirmation' | 'waiting_approval' | 'needs_choice' | 'refused';

export interface CopilotChatResult {
  reply: string;
  state: ChatState;
  toolEvents: ChatToolEvent[];
  meta: CopilotRunMeta;
  /**
   * Fala 10 (historia promptów): rozmowa, do której zapisano tę turę (`conversationId` z wejścia albo nowa) — kolejne
   * wiadomości z tym id dopisują się do tej samej rozmowy; null = bez zapisu (np. błąd zapisu, rozmowa innej osoby)
   */
  conversationId?: string | null;
}

// ── Ustawienia AI (Ustawienia → AI i pamięć → Model i limity) ───────────────

export const AI_MODEL_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:/@+-]{0,199}$/;
const aiPriceValueSchema = z
  .string()
  .trim()
  .regex(/^\d{1,6}(\.\d{1,4})?$/, { message: 'Cena w PLN, np. 12.50' });

/** Profil w zapisie: model + opcjonalny poziom rozumowania (Fala 9; brak = domyślny poziom modelu). */
const updateAiProfileInput = z
  .object({
    model: z.string().trim().regex(AI_MODEL_ID_RE, 'Nieprawidłowy identyfikator modelu'),
    reasoningEffort: aiReasoningEffortSchema.optional(),
  })
  .optional();

/** Zapis ustawień AI: pola + wersja wiersza (`null` = ustawienia jeszcze nie zapisane). */
export const updateAiSettingsInput = aiSettingsFieldsSchema
  .extend({
    version: z.number().int().positive().nullable(),
    profiles: z.object({
      fast: updateAiProfileInput,
      smart: updateAiProfileInput,
      exec: updateAiProfileInput,
    }),
    priceTable: z
      .record(
        z.object({
          inputPer1M: aiPriceValueSchema,
          // Fala 9: cena wejścia z cache (brak = jak wejście)
          cachedInputPer1M: aiPriceValueSchema.optional(),
          outputPer1M: aiPriceValueSchema,
        }),
      )
      .refine((t) => Object.keys(t).length <= 30, { message: 'Najwyżej 30 modeli w cenniku' })
      .refine((t) => Object.keys(t).every((k) => AI_MODEL_ID_RE.test(k)), {
        message: 'Nieprawidłowy identyfikator modelu w cenniku',
      }),
  })
  .strict();
export type UpdateAiSettingsInput = z.input<typeof updateAiSettingsInput>;

export interface AiProviderOption {
  id: AiProviderId;
  state: AiProviderState;
  /** skąd klucz: integracja workspace'u, zmienna środowiska instalacji, wbudowany (fake) */
  source: 'integration' | 'env' | 'builtin' | null;
  capabilities: AiCapabilities | null;
  /** można wybrać (fake zawsze; realny — gdy podłączony) */
  selectable: boolean;
  message: string | null;
}

export interface AiSettingsView {
  /** `workspace` = zapisane ustawienia, `env` = ustawienia instalacji (brak wiersza) */
  source: 'workspace' | 'env';
  version: number | null;
  fields: AiSettingsFields;
  providers: AiProviderOption[];
  active: { provider: string; state: AiProviderState; simulated: boolean; message: string | null };
  /** możliwości wymagane przez akcje Copilota (do opisu w UI) */
  required: Record<CopilotAction, (keyof AiCapabilities)[]>;
  canEdit: boolean;
  /** kwoty (budżet, cennik) widoczne tylko z „Widzi stawki i kwoty” */
  showMoney: boolean;
}

export const aiUsageInput = z
  .object({
    month: z
      .string()
      .regex(/^\d{4}-\d{2}$/, 'Miesiąc w formacie RRRR-MM')
      .optional(),
  })
  .strict()
  .default({});

export interface AiUsageView {
  month: string;
  /** zakres: cały workspace (owner/admin/manager) albo tylko ja */
  scope: 'workspace' | 'mine';
  jobs: number;
  failed: number;
  budgetExceeded: number;
  simulatedJobs: number;
  costPln?: string;
  budgetPln?: string | null;
  remainingPln?: string | null;
  byKind: { kind: string; jobs: number; costPln?: string }[];
  byPerson: {
    userId: string;
    name: string;
    jobs: number;
    costPln?: string;
    /** limit osoby (członkostwo albo domyślny z ustawień); null = bez limitu */
    limitPln?: string | null;
  }[];
}

export const listAiJobsInput = z
  .object({
    kind: z.string().trim().min(1).max(40).optional(),
    status: z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled', 'budget_exceeded']).optional(),
    userId: idSchema.optional(),
    clientId: idSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    limit: z.number().int().min(1).max(100).default(30),
    /** kursor: id ostatniego wiersza poprzedniej strony */
    cursor: idSchema.optional(),
    /** dopisywane przez `useInfiniteQuery` (tRPC v11); lista zawsze od najnowszych */
    direction: z.enum(['forward', 'backward']).optional(),
  })
  .strict()
  .default({});
export type ListAiJobsInput = z.input<typeof listAiJobsInput>;

export const aiJobIdInput = z.object({ id: idSchema }).strict();

export interface AiJobListItem {
  id: string;
  kind: string;
  status: AiJobStatus;
  provider: string;
  model: string;
  profile: AiProfile;
  inputSummary: string;
  createdAt: Date;
  finishedAt: Date | null;
  createdBy: { userId: string; name: string; initials: string; avatarColor: string | null } | null;
  client: { id: string; name: string } | null;
  tokensIn: number;
  tokensOut: number;
  costPln?: string;
  simulated: boolean;
  iterations: number;
  error: string | null;
}

export interface AiJobListResult {
  items: AiJobListItem[];
  nextCursor: string | null;
}

/** Szczegóły zadania AI — bez pełnego wejścia i bez prywatnego toku rozumowania (nie zapisujemy go). */
export interface AiJobDetail extends AiJobListItem {
  contextVersion: string | null;
  durationMs: number | null;
  output: {
    text: string | null;
    toolCalls: string[];
    sources: ContextSource[];
    truncated: boolean;
    priceMissing: boolean;
  };
}

/** Kolejka zadań utrzymania rejestru AI (worker). */
export const AI_QUEUES = { reap: 'ai.reap', retention: 'ai.retention' } as const;
/** Zadanie AI „running” starsze niż to = przerwane (proces zatrzymany), status `failed`. */
export const AI_JOB_STALE_AFTER_MS = 15 * 60 * 1000;
/** Retencja treści zadań AI (DECISIONS: wejścia AI 180 dni); koszt i tokeny zostają. */
export const AI_JOB_RETENTION_DAYS = 180;
